import { useMemo, useEffect } from 'react';
import styled from '@emotion/styled';
import { Stack, tokens } from '@hotjar/hotsauce';
import type { AllSectionsProps, FormValidation } from './Steps';
import { EmailRequest, Invitation, MainQuestion, ThankYou } from './Steps';

export interface StepsSectionProps extends AllSectionsProps {
  onValidateForm: (validation: boolean) => void;
}

const MainContainer = styled(Stack)`
  width: calc(100% - 390px);
  min-height: 650px;
  padding: ${tokens.spacer.x3} 72px;
`;

type ValidationString = string | undefined | null;

const validateRequiredString = (value: ValidationString) => !!value && value.length > 0;

export interface ValidationResult {
  invitationMessage: boolean;
  mainQuestion: boolean;
  emailRequest: boolean;
  additionalMessage: boolean;
  thankyouMessage: boolean;
  showLegal: boolean;
}

export interface ValidationProps {
  invitationMessage: ValidationString;
  mainQuestion: ValidationString;
  showEmailRequest: boolean;
  emailRequest: ValidationString;
  showAdditionalMessage: boolean;
  additionalMessage: ValidationString;
  thankyouMessage: ValidationString;
  showLegal: boolean;
  siteHasLegalDetails: boolean;
}

export const validateSteps = (props: ValidationProps): ValidationResult => {
  const {
    invitationMessage,
    mainQuestion,
    emailRequest,
    showEmailRequest,
    showAdditionalMessage,
    additionalMessage,
    thankyouMessage,
    showLegal,
    siteHasLegalDetails,
  } = props;

  return {
    invitationMessage: validateRequiredString(invitationMessage),
    mainQuestion: validateRequiredString(mainQuestion),
    emailRequest: !showEmailRequest || validateRequiredString(emailRequest),
    additionalMessage: !showAdditionalMessage || validateRequiredString(additionalMessage),
    thankyouMessage: validateRequiredString(thankyouMessage),
    showLegal: !(showLegal && !siteHasLegalDetails),
  };
};

const isFormValid = (formValidation: ValidationResult) =>
  Object.values(formValidation).reduce(
    (isAllValid, isStepValid) => isAllValid && isStepValid,
    true,
  );

export const areStepsValid = (props: ValidationProps): boolean => {
  const formValidation: FormValidation = validateSteps(props);
  return isFormValid(formValidation);
};

export const StepsSection = ({ onValidateForm, ...props }: StepsSectionProps): JSX.Element => {
  const formValidation: FormValidation = useMemo(() => validateSteps(props), [props]);

  useEffect(() => {
    onValidateForm(isFormValid(formValidation));
  }, [formValidation, onValidateForm]);

  return (
    <MainContainer spacing="x5" direction="column">
      <Invitation {...props} formValidation={formValidation} />
      <MainQuestion {...props} formValidation={formValidation} />
      <EmailRequest {...props} formValidation={formValidation} />
      <ThankYou {...props} formValidation={formValidation} />
    </MainContainer>
  );
};
